import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectOption } from '../ui/select';
import { formatCurrency } from '../../lib/utils';
import { 
  Eye, 
  Edit, 
  FileText,
  User,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';

const DashboardRecentProjects = ({ projects }) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in-progress': return 'warning';
      case 'pending': return 'secondary';
      default: return 'secondary';
    }
  };

  const getProjectStatus = (project) => {
    const paidAmount = project.totalPaidAmount || 0;
    const totalInstallmentAmount = project.totalPaymentAmount || 0;
    
    if (totalInstallmentAmount === 0) {
      return 'pending';
    }
    
    if (paidAmount >= totalInstallmentAmount) {
      return 'completed';
    }
    
    if (paidAmount > 0) {
      return 'in-progress';
    }
    
    return 'pending';
  };

  const statusText = {
    'completed': 'เสร็จสิ้น',
    'in-progress': 'กำลังดำเนินการ',
    'pending': 'รอดำเนินการ'
  };

  // Pagination calculations
  const totalItems = projects.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProjects = projects.slice(startIndex, endIndex);

  // Reset to first page when itemsPerPage changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-3 sm:space-y-0">
          <div className="flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>รายการโปรเจค</span>
            <Badge variant="secondary" className="ml-2">
              {totalItems} โปรเจค
            </Badge>
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-600">แสดง:</span>
              <Select 
                value={itemsPerPage.toString()} 
                onValueChange={(value) => setItemsPerPage(parseInt(value))}
                className="w-16"
              >
                <SelectOption value="3">3</SelectOption>
                <SelectOption value="5">5</SelectOption>
                <SelectOption value="10">10</SelectOption>
              </Select>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/projects')}
              className="text-sm font-medium"
            >
              ดูทั้งหมด
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {paginatedProjects.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 mb-4">
              <FileText className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">ยังไม่มีโปรเจค</h3>
            <p className="text-gray-500 mb-4">เริ่มต้นด้วยการเพิ่มโปรเจคแรกของคุณ</p>
            <Button onClick={() => navigate('/projects/new')}>
              เพิ่มโปรเจคใหม่
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {paginatedProjects.map((project) => {
              const status = getProjectStatus(project);
              const progressPercentage = project.totalPaymentAmount > 0 
                ? Math.round((project.totalPaidAmount / project.totalPaymentAmount) * 100) 
                : 0;

              return (
                <div 
                  key={project.id}
                  className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/projects/${project.id}`)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 mb-3">
                        <h3 className="font-semibold text-gray-900 text-sm sm:text-base mb-1 sm:mb-0">{project.project_name}</h3>
                        <Badge variant={getStatusColor(status)} className="text-xs w-fit">
                          {statusText[status]}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-xs sm:text-sm text-gray-600 mb-3">
                        <div className="flex items-center space-x-1">
                          <User className="h-3 w-3 sm:h-4 sm:w-4" />
                          <span className="truncate">{project.client_name}</span>
                        </div>
                        {project.sales_name && (
                          <div className="flex items-center space-x-1">
                            <span>Sale: {project.sales_name}</span>
                          </div>
                        )}
                        {project.start_date && (
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                            <span>เริ่ม: {new Date(project.start_date).toLocaleDateString('th-TH')}</span>
                          </div>
                        )}
                        {project.end_date && (
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                            <span>จบ: {new Date(project.end_date).toLocaleDateString('th-TH')}</span>
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
                        <div>
                          <p className="text-gray-500 text-xs">ราคาโปรเจค</p>
                          <p className="font-semibold text-gray-900 text-xs sm:text-sm">
                            {formatCurrency(project.project_price || 0)}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs">ยอดรับจริง</p>
                          <p className="font-semibold text-blue-600 text-xs sm:text-sm">
                            {formatCurrency(project.actual_received || 0)}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs">ชำระแล้ว</p>
                          <p className="font-semibold text-green-600 text-xs sm:text-sm">
                            {formatCurrency(project.totalPaidAmount || 0)}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs">งวด</p>
                          <p className="font-semibold text-purple-600 text-xs sm:text-sm">
                            {project.paidInstallmentsCount || 0}/{project.totalInstallmentsCount || 0}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs">ค่าบริการ</p>
                          <p className="font-semibold text-orange-600 text-xs sm:text-sm">
                            {formatCurrency(project.total_company_fee_amount || 0)}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs">ประเภท</p>
                          <p className="font-semibold text-indigo-600 text-xs sm:text-sm">
                            {project.price_type === 'include_vat' ? 'รวม VAT' : 'ไม่รวม VAT'}
                          </p>
                        </div>
                      </div>

                      {project.totalPaymentAmount > 0 && (
                        <div className="mt-3">
                          <div className="flex justify-between text-xs text-gray-600 mb-1">
                            <span>ความคืบหน้าการชำระ</span>
                            <span>{progressPercentage}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-green-500 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${progressPercentage}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center space-x-1 sm:space-x-2 ml-2 sm:ml-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/projects/${project.id}`);
                        }}
                        className="h-7 w-7 sm:h-8 sm:w-8 p-0"
                      >
                        <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/projects/${project.id}/edit`);
                        }}
                        className="h-7 w-7 sm:h-8 sm:w-8 p-0"
                      >
                        <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 border-t pt-4">
            <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
              {/* Pagination Info */}
              <div className="text-sm text-gray-700">
                แสดง <span className="font-semibold">{startIndex + 1}</span> ถึง <span className="font-semibold">{Math.min(endIndex, totalItems)}</span> จาก <span className="font-semibold">{totalItems}</span> รายการ
              </div>
              
              {/* Pagination Controls */}
              <div className="flex items-center space-x-2">
                {/* First Page */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="h-8 w-8 p-0"
                  title="หน้าแรก"
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                
                {/* Previous Page */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="h-8 w-8 p-0"
                  title="หน้าก่อน"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                
                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {(() => {
                    const maxVisible = 3;
                    const pages = [];
                    
                    if (totalPages <= maxVisible) {
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(i);
                      }
                    } else {
                      let start = Math.max(1, currentPage - 1);
                      let end = Math.min(totalPages, currentPage + 1);
                      
                      if (currentPage <= 2) {
                        end = Math.min(totalPages, 3);
                      } else if (currentPage >= totalPages - 1) {
                        start = Math.max(1, totalPages - 2);
                      }
                      
                      for (let i = start; i <= end; i++) {
                        pages.push(i);
                      }
                    }
                    
                    return pages.map(pageNum => (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        className={`h-8 w-8 p-0 text-xs font-medium ${
                          currentPage === pageNum 
                            ? 'bg-blue-600 text-white hover:bg-blue-700' 
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </Button>
                    ));
                  })()}
                </div>

                {/* Next Page */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="h-8 w-8 p-0"
                  title="หน้าถัดไป"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                
                {/* Last Page */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="h-8 w-8 p-0"
                  title="หน้าสุดท้าย"
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            {/* Page Info */}
            <div className="mt-3 text-center text-xs text-gray-500">
              หน้า {currentPage} จาก {totalPages} หน้า
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DashboardRecentProjects;
